import React, { useState } from 'react';

import { Box, Typography, Button, TextField } from '@mui/material';
import { LabelsInput, OrdTransferList } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { dataSchema } from '@ragu-components/ord-types';
import { useRecordContext, useTranslate, useGetRecordRepresentation } from 'react-admin';
import { useFormContext } from 'react-hook-form';

import { useAgentsData } from './hooks/useAgentsData';
import { useAgentsOperations } from './hooks/useAgentsOperations';
import { agentSettingsFields } from './utils/agentSettings';

import type { AgentSettingField } from './utils/agentSettings';
import type { Queue, AgentSettings } from '@ragu-components/ord-types';

type AgentSettingsFormState = Record<AgentSettingField, string>;

/**
 * Safely converts a value to a number, returning undefined if invalid.
 */
const getNumericValue = (value: unknown): number | undefined => {
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

/**
 * Extract default values from the dataSchema once.
 */
const defaultSettings = agentSettingsFields.reduce<Partial<Record<AgentSettingField, number>>>((acc, field) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
  const schemaDefault = (dataSchema.AgentSettings?.properties as any)?.[field]?.default as unknown;
  const val = getNumericValue(schemaDefault);
  if (val !== undefined) {
    acc[field] = val;
  }
  return acc;
}, {});

/**
 * Creates the initial form state from existing settings or defaults.
 * Converts numbers to strings for TextField compatibility.
 */
const createAgentSettingsState = (settings: Record<string, unknown> = {}): AgentSettingsFormState => {
  return agentSettingsFields.reduce<Partial<AgentSettingsFormState>>((acc, field) => {
    const val = getNumericValue(settings[field]) ?? defaultSettings[field];
    acc[field] = val !== undefined ? String(val) : '';
    return acc;
  }, {}) as AgentSettingsFormState;
};

/**
 * Converts the form string state back to numbers for the API.
 */
const parseStateToOverrides = (state: AgentSettingsFormState): AgentSettings => {
  return agentSettingsFields.reduce<Partial<AgentSettings>>((acc, field) => {
    if (field === 'initial_paused') {
      acc[field] = state[field] as unknown as boolean;
      return acc;
    }
    acc[field] = Number(state[field] ?? 0);
    return acc;
  }, {}) as AgentSettings;
};

const hasListChanged = (current: string[], next: string[]): boolean => {
  if (current.length !== next.length) {
    return true;
  }
  const currentSet = new Set(current);
  return next.some((id) => !currentSet.has(id));
};

const haveListsChanged = (
  nextUsers: string[],
  nextGroups: string[],
  initialUsers: string[],
  initialGroups: string[]
): boolean => {
  return hasListChanged(initialUsers, nextUsers) || hasListChanged(initialGroups, nextGroups);
};

/**
 * Validates that all setting fields have valid numeric values.
 */
const isAgentSettingsStateValid = (state: AgentSettingsFormState): boolean =>
  agentSettingsFields.every((field) => {
    const value = state[field].trim();
    return value !== '' && Number.isFinite(Number(value));
  });

const AssignAgentsForm = ({
  onAgentsUpdateEvent = () => {},
}: {
  onAgentsUpdateEvent?: () => void;
}): React.ReactElement => {
  const record = useRecordContext<Queue>();
  const translate = useTranslate();
  const { getValues } = useFormContext();

  // Local state for current selections
  const [currentAgents, setCurrentAgents] = useState<string[]>([]);
  const [currentUserIds, setCurrentUserIds] = useState<string[]>([]);
  const [currentGroupIds, setCurrentGroupIds] = useState<string[]>([]);
  const [labelIds, setLabelIds] = useState<string[] | undefined>(undefined);
  const initialSelectionsRef = React.useRef<{ users: string[]; groups: string[] }>({ users: [], groups: [] });

  const queueAgentSettings = React.useMemo(
    () => createAgentSettingsState(record?.settings as Record<string, unknown> | undefined),
    [record]
  );
  const [agentSettingsState, setAgentSettingsState] = useState<AgentSettingsFormState>(queueAgentSettings);
  const settingsStateIsValid = React.useMemo(() => isAgentSettingsStateValid(agentSettingsState), [agentSettingsState]);

  // Data fetching and processing
  const { agents, userIds, groupIds, agentPairs, loaded } = useAgentsData(record);

  // Operations handling
  const { updateAgents, updateDisabled, setUpdateDisabled } = useAgentsOperations({
    // We can cast it to Queue because this form is only called in the Queue edit context
    record: record as unknown as Queue,
    onAgentsUpdateEvent,
    onUserDeleteFailed: (failedUserIds: string[]) => {
      // For deletion failures: return failed users back to the selection
      setCurrentUserIds((prev) => [...new Set([...prev, ...failedUserIds])]);
    },
    onGroupDeleteFailed: (failedGroupIds: string[]) => {
      // For deletion failures: return failed groups back to the selection
      setCurrentGroupIds((prev) => [...new Set([...prev, ...failedGroupIds])]);
    },
    onUserCreateFailed: (failedUserIds: string[]) => {
      // For creation failures: remove failed users from selection
      setCurrentUserIds((prev) => prev.filter((id) => !failedUserIds.includes(id)));
    },
    onGroupCreateFailed: (failedGroupIds: string[]) => {
      // For creation failures: remove failed groups from selection
      setCurrentGroupIds((prev) => prev.filter((id) => !failedGroupIds.includes(id)));
    },
  });

  const groupRepresentation = useGetRecordRepresentation(Resources.groups);
  const userRepresentation = useGetRecordRepresentation(Resources.users);

  // Initialize current selections and labels when data loads
  React.useEffect(() => {
    if (loaded) {
      initialSelectionsRef.current = { users: userIds, groups: groupIds };
      setCurrentUserIds(userIds);
      setCurrentGroupIds(groupIds);
      setUpdateDisabled(true);
    }
  }, [loaded, userIds, groupIds, setUpdateDisabled]);

  React.useEffect(() => {
    if (loaded) {
      setCurrentAgents(agents.map((agent) => agent.id as string));
    }
  }, [loaded, agents]);

  // Get initial labels value once on component mount
  React.useEffect(() => {
    const initialLabels = getValues('labels');
    if (initialLabels !== undefined) {
      setLabelIds(initialLabels as string[]);
    }
  }, [getValues]);

  React.useEffect(() => {
    setAgentSettingsState(queueAgentSettings);
  }, [queueAgentSettings]);

  const handleUserIdsChange = (selectedIds: string[]): void => {
    setCurrentUserIds(selectedIds);
    setCurrentAgents([...currentGroupIds, ...selectedIds]);
    const listsChanged = haveListsChanged(
      selectedIds,
      currentGroupIds,
      initialSelectionsRef.current.users,
      initialSelectionsRef.current.groups
    );
    setUpdateDisabled(!listsChanged);
  };

  const handleGroupIdsChange = (selectedIds: string[]): void => {
    setCurrentGroupIds(selectedIds);
    setCurrentAgents([...currentUserIds, ...selectedIds]);
    const listsChanged = haveListsChanged(
      currentUserIds,
      selectedIds,
      initialSelectionsRef.current.users,
      initialSelectionsRef.current.groups
    );
    setUpdateDisabled(!listsChanged);
  };

  const handleLabelsChange = (selectedIds: string[] | undefined): void => {
    setLabelIds(selectedIds);
  };

  const handleAgentSettingChange =
    (field: AgentSettingField) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const { value } = event.target;
      setAgentSettingsState((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleUpdate = (): void => {
    if (!settingsStateIsValid) {
      return;
    }
    const normalizedAgentSettings = parseStateToOverrides(agentSettingsState);
    void updateAgents(currentUserIds, currentGroupIds, agentPairs, labelIds ?? [], normalizedAgentSettings);
  };

  return (
    <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'stretch', padding: 2 }}>
      {/* This condition prevents rendering with incorrect list */}
      {loaded && (currentAgents.length === 0 || currentUserIds.length !== 0 || currentGroupIds.length !== 0) && (
        <>
          <Typography>{translate('resources.users.name')}</Typography>
          <OrdTransferList
            key={`user-transfer-list-${JSON.stringify(currentUserIds)}`}
            selectedIds={currentUserIds}
            fiqlArgument={'username'}
            resource={Resources.users}
            onChange={handleUserIdsChange}
            optionText={userRepresentation}
          />
          <Typography>{translate('resources.groups.name')}</Typography>
          <OrdTransferList
            key={`group-transfer-list-${JSON.stringify(currentGroupIds)}`}
            selectedIds={currentGroupIds}
            fiqlArgument={'name'}
            resource={Resources.groups}
            onChange={handleGroupIdsChange}
            optionText={groupRepresentation}
          />
          <LabelsInput onChange={handleLabelsChange} helperText={'resources.agents.helperTexts.bulkAssignLabels'} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
            {agentSettingsFields.map((field) => (
              <TextField
                key={field}
                type='number'
                value={agentSettingsState[field]}
                label={translate(`resources.agents.fields.settings.${field}`)}
                helperText={translate(`resources.agents.helperTexts.settings.${field}`)}
                onChange={handleAgentSettingChange(field)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0 }}
              />
            ))}
          </Box>
          <Button onClick={handleUpdate} disabled={updateDisabled || !settingsStateIsValid} variant='contained'>
            {translate('ra.action.update')}
          </Button>
        </>
      )}
    </Box>
  );
};

export default AssignAgentsForm;
